/*
 * WHAT A FAILED MODEL CALL ACTUALLY SAID, with the transport's framing taken off.
 *
 * A provider's refusal reaches a reader wrapped up to twice. `ProviderRunError` prefixes
 * "provider run error: ", and when the refusal came from our own edge rather than the provider, the
 * OpenRouter SDK wraps the response before that:
 *
 *     provider run error: API error occurred: Status 402 Content-Type text/plain;charset=UTF-8.
 *     Body: out of credit — top up to continue
 *
 * The one sentence written by whoever knew what went wrong arrived behind a content type. And a
 * refusal the platform could have explained arrived as the provider's internals — an adapter class
 * name is what a person was shown for "you picked a batch-only model":
 *
 *     provider run error: google/gemini-3.8-flash:batch cannot be used with the chat/completions
 *     endpoint (adapter VertexGeminiBatchAdapter).
 *
 * PARSED FROM THE MESSAGE, NOT CARRIED ON THE ERROR, because the message is the only thing that
 * survives: a transcript stores a failed turn as a string and a run's summary is a string, so a field
 * on the error object is gone by the time anybody reads the failure.
 *
 * Facts only, in no language. The browser says them in the reader's (turn-view.ts `humanError`);
 * server-side callers, which write English everywhere else, use `failureSentence`.
 */
import { chatTwin } from './routing';

interface FailureCommon {
  /** The words of whoever refused, with the framing removed. Shown when nothing better is known. */
  detail: string;
  /** The HTTP status, when the framing carried one. */
  status?: number;
  /** Exactly what was thrown. */
  raw: string;
}

export type ProviderFailure =
  /** The provider will not serve this model on the endpoint it was called on — the request was fine,
   *  the model choice was not. Retrying cannot help; choosing another model does. */
  | (FailureCommon & {
      kind: 'model_not_chat_capable';
      /** The model the provider refused. */
      model: string;
      /** The listing of the same model that DOES answer a chat (`vendor/model:batch` → `vendor/model`). */
      useInstead?: string;
      /** The endpoint the provider refused the model on, as it named it (`chat/completions`). */
      endpoint?: string;
      /** The provider-side adapter the refusal names — which upstream the listing was pinned to.
       *  Means nothing to a person and a great deal to whoever has to explain the failure. */
      adapter?: string;
    })
  /** Nothing this parser recognises. `detail` is still the best thing to show. */
  | (FailureCommon & { kind: 'unrecognised'; model?: never; useInstead?: never; endpoint?: never; adapter?: never });

export type FailureKind = ProviderFailure['kind'];

const RUN_ERROR_PREFIX = /^provider run error:\s*/i;
/** The OpenRouter SDK's wrapper for a non-2xx response. What separates the content type from `Body:`
 *  is ". " for a short body and a newline for a long one, so neither is assumed. */
const SDK_WRAPPER = /^API error occurred: Status (\d{3})\b[\s\S]*?Body:\s*([\s\S]*)$/;

/** OpenRouter's words for a model it will not serve on this endpoint. */
const WRONG_ENDPOINT = /([\w.-]+\/[\w.:-]+) cannot be used with the ([\w/.-]+) endpoint(?: \(adapter ([\w.-]+)\))?/;
/** Our edge's words for the same refusal, made before the call leaves (llm.ts). */
const EDGE_BATCH_ONLY = /([\w.-]+\/[\w.:-]+) only takes batch jobs/;

export function parseProviderFailure(raw: string): ProviderFailure {
  let detail = raw.trim().replace(RUN_ERROR_PREFIX, '');
  let status: number | undefined;
  const wrapped = SDK_WRAPPER.exec(detail);
  if (wrapped) {
    status = Number(wrapped[1]);
    detail = wrapped[2].trim();
  }
  const known = { detail, raw, ...(status === undefined ? {} : { status }) };

  const upstream = WRONG_ENDPOINT.exec(detail);
  const edge = upstream ? null : EDGE_BATCH_ONLY.exec(detail);
  const model = upstream?.[1] ?? edge?.[1];
  if (model) {
    const twin = chatTwin(model);
    return {
      ...known,
      kind: 'model_not_chat_capable',
      model,
      ...(twin === model ? {} : { useInstead: twin }),
      ...(upstream?.[2] ? { endpoint: upstream[2] } : {}),
      ...(upstream?.[3] ? { adapter: upstream[3] } : {}),
    };
  }
  return { ...known, kind: 'unrecognised' };
}

/** The failure as one English sentence with a next step in it, for server-side text — a run's
 *  summary, which a parent agent reads as well as a person. */
export function failureSentence(f: ProviderFailure): string {
  if (f.kind === 'model_not_chat_capable') {
    return f.useInstead
      ? `${f.model} only takes batch jobs, so it cannot answer a chat. Choose ${f.useInstead} instead — the same model, served for chat.`
      : `${f.model} cannot be used for chat. Choose a different model.`;
  }
  return f.detail;
}
